import React from 'react';
import { IGridConfig } from 'components/ComparisonGrid';
import { NSDropdown } from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { COST_BASIS_TYPES } from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import { ProFormaModelIds } from 'constants/proForma';
import { IOptionNumberValue } from 'bricks/types';
import { SimplifiedScenario } from 'components/DealScenarioDropdown/types';
import { useScenarioContext } from './context/ScenarioComparisonProvider';
import {
    formatCurrencyComparison, formatLoanTerm, formatPercentageComparison, roundFormatComparison,
} from './helper';

export const MAX_SCENARIOS_FOR_PDF = 5;

function headerDefaultFormatter(data: SimplifiedScenario): JSX.Element {
    const { getAvailableOptions, switchScenario, removeScenario } = useScenarioContext();

    const handleSelection = (scenario: IOptionNumberValue) => {
        switchScenario(data.id, { id: scenario.value, name: scenario.label });
    };

    const handleRemove = () => {
        removeScenario(data.id);
    };

    return (
        <div className="d-flex align-items-center w-100 h-100 mr-2 p-2">
            <NSDropdown
                options={getAvailableOptions()}
                selectedOption={{ label: data.name, value: data.id }}
                handleSelectOption={handleSelection}
                placeholder="Select scenario"
                toggleClassName="px-1 py-0 mr-1 m-0 ScenarioComparison__badge-selector"
            />
            <FontAwesomeIcon className="ScenarioComparison__x-mark-button" icon={faXmark} size="lg" onClick={handleRemove} />
        </div>
    );
}

export const gridConfig: Omit<IGridConfig, 'data'> = {
    headerRowFormatter: headerDefaultFormatter,
    groups: [
        {
            groupLabel: 'Returns',
            rows: [
                { label: 'Return on cost', formatter: boe => formatPercentageComparison(boe.returnOnCost * 100), fieldName: 'returnOnCost' },
                { label: 'Return on equity', formatter: boe => formatPercentageComparison(boe.returnOnEquity * 100), fieldName: 'returnOnEquity' },
                { label: 'Profit return', formatter: boe => formatCurrencyComparison(boe.profitReturn), fieldName: 'profitReturn' },
            ],
        },
        {
            groupLabel: 'Propery size',
            rows: [
                { label: 'Built gross SF', formatter: boe => roundFormatComparison(boe.builtSf), fieldName: 'builtSf' },
                {
                    label: 'Building efficiency',
                    formatter: boe => formatPercentageComparison(boe.buildingEfficiency * 100),
                    fieldName: 'buildingEfficiency',
                },
                { label: 'Net rentable SF', formatter: boe => roundFormatComparison(boe.netRentableSf), fieldName: 'netRentableSf' },
            ],
        },
        {
            groupLabel: 'Income',
            rows: [
                {
                    label: 'Rent price per unit',
                    formatter: boe => (boe.propertyType.id === ProFormaModelIds.APARTMENT ? formatCurrencyComparison(boe.monthlyRentPricePerUnit) : 'N/A'),
                    fieldName: 'monthlyRentPricePerUnit',
                },
                {
                    label: 'Rent price per SF',
                    formatter: boe => (boe.propertyType.id !== ProFormaModelIds.APARTMENT ? formatCurrencyComparison(boe.monthlyRentPricePerSf) : 'N/A'),
                    fieldName: 'monthlyRentPricePerSf',
                },
                {
                    label: 'Units',
                    formatter: boe => (boe.propertyType.id === ProFormaModelIds.APARTMENT ? boe.numberOfUnits : 'N/A'),
                },
                {
                    label: 'SF per unit',
                    formatter: boe => (boe.propertyType.id === ProFormaModelIds.APARTMENT ? boe.sfPerUnit : 'N/A'),
                    fieldName: 'sfPerUnit',
                },
                {
                    label: 'Gross potential rent',
                    formatter: boe => formatCurrencyComparison(boe.grossPotentialRent),
                    fieldName: 'grossPotentialRent',
                },
            ],
        },
        {
            groupLabel: 'Expenses',
            rows: [
                {
                    label: 'Expenses per unit',
                    formatter: boe => (
                        boe.propertyType.id === ProFormaModelIds.APARTMENT ? formatCurrencyComparison(boe.monthlyExpenseCostPerUnit) : 'N/A'
                    ),
                    fieldName: 'monthlyExpenseCostPerUnit',
                },
                {
                    label: 'Expenses per SF',
                    formatter: boe => (boe.propertyType.id !== ProFormaModelIds.APARTMENT ? formatCurrencyComparison(boe.monthlyExpenseCostPerSf) : 'N/A'),
                    fieldName: 'monthlyExpenseCostPerSf',
                },
                { label: 'Expenses total', formatter: boe => formatCurrencyComparison(boe.expensesTotal), fieldName: 'expensesTotal' },
                {
                    label: 'Net operating income',
                    formatter: boe => formatCurrencyComparison(boe.netOperatingIncome),
                    fieldName: 'netOperatingIncome',
                },
            ],
        },
        {
            groupLabel: 'Exit value',
            rows: [
                { label: 'Exit cap rate', formatter: boe => formatPercentageComparison(boe.assumedExitCapRate * 100), fieldName: 'assumedExitCapRate' },
                { label: 'Exit valuation', formatter: boe => formatCurrencyComparison(boe.exitValuation), fieldName: 'exitValuation' },
            ],
        },
        {
            groupLabel: 'Development costs',
            rows: [
                { label: 'Acquisition costs', formatter: boe => formatCurrencyComparison(boe.acquisition), fieldName: 'acquisition' },
                {
                    label: 'Hard cost per SF',
                    formatter: boe => formatCurrencyComparison(boe.hardCostsSubdiv === COST_BASIS_TYPES.PER_SF ? boe.hardCosts : null),
                    fieldName: 'hardCostsPerSf',
                },
                { label: 'Total hard costs', formatter: boe => formatCurrencyComparison(boe.hardCostsTotal), fieldName: 'hardCostsTotal' },
                {
                    label: 'Soft costs per SF',
                    formatter: boe => formatCurrencyComparison(boe.softCostsSubdiv === COST_BASIS_TYPES.PER_SF ? boe.softCosts : null),
                    fieldName: 'softCostsPerSf',
                },
                { label: 'Total soft costs', formatter: boe => formatCurrencyComparison(boe.softCostsTotal), fieldName: 'softCostsTotal' },
                { label: 'Developer fee', formatter: boe => formatPercentageComparison(boe.developerFeeRate * 100), fieldName: 'developerFeeRate' },
                { label: 'Total developer fee', formatter: boe => formatCurrencyComparison(boe.developerFeeTotal), fieldName: 'developerFeeTotal' },
                { label: 'APR', formatter: boe => formatPercentageComparison(boe.annualPercentageRate * 100), fieldName: 'annualPercentageRate' },
                {
                    label: 'Average loan balance',
                    formatter: boe => formatPercentageComparison(boe.averageLoanBalance * 100),
                    fieldName: 'averageLoanBalance',
                },
                {
                    label: 'Loan term',
                    formatter: boe => formatLoanTerm(boe),
                    fieldName: 'loanTermMonths',
                },
                {
                    label: 'Interest reserve total',
                    formatter: boe => formatCurrencyComparison(boe.interestReserveTotal),
                    fieldName: 'interestReserveTotal',
                },
                {
                    label: 'Total development costs',
                    formatter: boe => formatCurrencyComparison(boe.totalDevelopmentCosts),
                    fieldName: 'totalDevelopmentCosts',
                },
            ],
        },
        {
            groupLabel: 'Construction Financing',
            rows: [
                { label: 'LTC', formatter: boe => formatPercentageComparison(boe.loanToCost * 100), fieldName: 'loanToCost' },
                { label: 'Loan amount', formatter: boe => formatCurrencyComparison(boe.loanAmount), fieldName: 'loanAmount' },
                { label: 'Required equity', formatter: boe => formatCurrencyComparison(boe.requiredEquity), fieldName: 'requiredEquity' },
            ],
        },
    ],
};

export const highlightCriteria: Record<string, 'greatest' | 'least'> = {
    returnOnCost: 'greatest',
    returnOnEquity: 'greatest',
    profitReturn: 'greatest',
    exitValuation: 'greatest',
    buildingEfficiency: 'greatest',
    netOperatingIncome: 'greatest',
    totalDevelopmentCosts: 'least',
};
